// File: frontend/src/hooks/useIsPwaStandalone.ts

import { useEffect, useState } from "react";

/**
 * True when the app is running as an installed PWA rather than inside a normal
 * browser tab.
 *
 * Two checks are needed. The display-mode media query is the standard and
 * covers Android/desktop plus iOS 16.4+; navigator.standalone is the older
 * non-standard iOS flag, kept because it still reports correctly on devices
 * where the media query is unreliable.
 */
export function useIsPwaStandalone(): boolean {
  const [isStandalone, setIsStandalone] = useState<boolean>(() => detect());

  useEffect(() => {
    // The mode can change without a reload (for instance when the user opens
    // the installed app from a link that had been rendering in a tab).
    const query = window.matchMedia("(display-mode: standalone)");
    const handleChange = () => setIsStandalone(detect());
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  return isStandalone;
}

function detect(): boolean {
  if (typeof window === "undefined") return false;
  const mediaMatch =
    typeof window.matchMedia === "function" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches);
  // iOS-only legacy flag; not in the TypeScript DOM lib, hence the cast.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mediaMatch || iosStandalone;
}